import { PrimaryDownloadCTAText, DownloadURL } from "constants/download";
import HeroAnimation from "images/homepage/HeroAnimation";
import HeroDucky from "images/homepage/HeroDucky";
import React from "react";
import styled from "styled-components";
import { FluidImageData } from "types";
import { OS, getDeviceOS } from "utils/detect-os";
import { URLwithUTMSuffix } from "utils/download";
import { Trans, useTranslation } from "react-i18next";
import greenHeart from "images/icons/green-heart.svg";

interface IProps {
    images: FluidImageData[];
    headingText: string;
    paragraphText: string;
    buttonText: string;
    backdropBackground?: string;
    backdropBackgroundImage?: string;
    duckyShieldColor: string;
}

const Container = styled.section.attrs({
    className: "container",
})`
    display: flex;
    align-items: stretch;
    flex-direction: column;
`;

const BackDrop = styled.div.attrs({
    className: "g-0 pt-0 pb-0 pt-lg-5 pb-lg-5",
})<{ backdropBackground?: string; backdropBackgroundImage?: string }>`
    overflow: hidden;
    color: #0c4401;
    border-radius: 20px;
    width: 100%;
    display: flex;
    align-items: center;

    background: ${(props) =>
        props.backdropBackground
            ? props.backdropBackground
            : props.backdropBackgroundImage
              ? `url(${props.backdropBackgroundImage}) repeat`
              : "none"};

    min-height: 450px;

    @media (max-width: 512px) {
        flex-direction: column;
        height: calc(100svh - 186px);
        justify-content: space-between;
        border-radius: 15px;

        @supports (-webkit-touch-callout: none) {
            height: calc(-webkit-fill-available - 186px);
        }
    }

    @media (min-width: 512px) and (max-width: 992px) {
        flex-direction: column;
        height: calc(100svh - 216px);
        border-radius: 15px;

        @supports (-webkit-touch-callout: none) {
            height: calc(-webkit-fill-available - 216px);
        }
    }

    @media (min-width: 1440px) {
        height: calc(100svh - 160px);
    }

    @media (min-width: 1920px) {
        height: 700px;
    }
`;

const EmptyBackDrop = styled.div.attrs({
    className: "g-0 pt-0 pb-0 pt-lg-5 pb-lg-5",
})<{ backdropBackground?: string; backdropBackgroundImage?: string }>`
    overflow: hidden;
    color: #0c4401;
    border-radius: 20px;
    width: 100%;
    display: flex;
    align-items: center;

    background: ${(props) =>
        props.backdropBackground
            ? props.backdropBackground
            : props.backdropBackgroundImage
              ? `url(${props.backdropBackgroundImage}) repeat`
              : "none"};

    min-height: 450px;

    @media (max-width: 512px) {
        flex-direction: column;
        height: calc(100svh - 186px);
        justify-content: space-between;
        border-radius: 15px;

        @supports (-webkit-touch-callout: none) {
            height: calc(-webkit-fill-available - 186px);
        }
    }

    @media (min-width: 512px) and (max-width: 992px) {
        flex-direction: column;
        height: calc(100svh - 216px);
        border-radius: 15px;

        @supports (-webkit-touch-callout: none) {
            height: calc(-webkit-fill-available - 216px);
        }
    }

    @media (min-width: 1440px) {
        height: calc(100svh - 160px);
    }

    @media (min-width: 1920px) {
        height: 700px;
    }
`;

const Heading = styled.h1`
    font-weight: 700;
    font-size: 40px;
    color: #fff !important;
    line-height: 100%;

    @media (max-width: 332px) {
        font-size: 35px;
    }

    @media (min-width: 1200px) {
        font-size: 55px;
    }

    @media (min-width: 1400px) {
        font-size: 63px;
    }
`;

const Highlight = styled.span`
    color: #fff;
`;

const HeroDuckyImage = styled(HeroDucky)<{ isAuthProp: boolean }>`
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;

    scale: 1;
    left: 32%;
    top: 20%;

    @media (max-width: 992px) {
        display: none;
    }

    @media (min-width: 992px) {
        left: -15%;
        top: 20%;
    }
    @media (min-width: 1200px) {
        left: -16%;
    }
    @media (min-width: 1400px) {
        left: -21%;
    }
`;

const Paragraph = styled.p<{ isAuthProp: boolean }>`
    font-weight: 500;
    font-size: 15px;
    padding: 4px;
    color: ${(props) => (props.isAuthProp ? "#fff" : "#0c4401")};
    margin: 0;

    @media (min-width: 332px) {
        font-size: 17px;
    }

    @media (min-width: 440px) {
        font-size: 18px;
    }

    @media (min-width: 768px) {
        font-size: 20px;
    }

    @media (max-width: 992px) {
        color: ${(props) => (props.isAuthProp ? "#fff" : "#000")};
    }

    @media (min-width: 1200px) {
        font-size: 24px;
    }

    @media (min-width: 1400px) {
        font-size: 26px;
    }
`;

const Picture = styled.picture<{
    aspectRatio: { light: string };
}>`
    z-index: 1:
    & > img {
        aspect-ratio: ${(props) => props.aspectRatio.light} / 1;
    }

    @media (max-width: 992px) {
        display: flex;
        justify-content: center;
    }
`;

const ImageContainer = styled.div.attrs({
    className: "col-md-7 p-0 d-flex position-relative",
})`
    justify-content: center;
    z-index: 1;

    @media (max-width: 992px) {
        width: 100%;
        overflow: hidden;
        position: relative;
    }

    @media (min-width: 992px) {
        justify-content: flex-start;
        left: 4%;
    }
    @media (min-width: 1024px) {
        justify-content: flex-start;
        left: 6%;
    }
    @media (min-width: 1200px) {
        left: 7%;
    }
    @media (min-width: 1400px) {
        left: 12%;
    }
`;

const Image = styled.img`
    position: relative;
    z-index: 1;

    @media (max-width: 512px) {
        width: 90%;
        height: auto;
        object-fit: cover;
        object-position: top; /* Aligns the image to show the top portion */
        transform: translateY(0%);
        margin-top: 8px;
    }

    @media (min-width: 512px) and (max-width: 992px) {
        width: 50%;
        height: auto;
        object-fit: cover;
        object-position: top; /* Aligns the image to show the top portion */
        transform: translateY(0%);
        margin-top: 8px;
    }

    @media (min-width: 992px) {
        top: 32%;
        scale: 1.5;
        width: 245px;
    }

    @media (min-width: 1440px) {
        top: 36%;
        scale: 1.5;
        width: 245px;
    }

    @media (min-width: 1920px) {
        top: 32%;
        scale: 1.5;
        width: 245px;
    }

    @media (min-width: 2560px) {
        top: 33%;
        scale: 1.5;
        width: 245px;
    }
`;

const CtaButton = styled.button`
    z-index: 2;
    border: none;
    background: linear-gradient(90deg, #000, #252525);
    padding: 16px 12px;
    color: #fff;
    border-radius: 15px;
    margin-top: 12px;
    font-weight: 600;
    font-size: 20px;
    font-family: Gilroy;
    width: 230px;
    position: relative;
    overflow: hidden;
    align-items: center;
    letter-spacing: 0.8px;

    &::before {
        content: " ";
        background: #9c9c9c;
        filter: blur(20px);
        position: absolute;
        left: 0;
        width: 12px;
        top: -100%;
        height: 400%;
        animation: shine 4s linear infinite;
    }

    @keyframes shine {
        0% {
            left: -50%;
            rotate: 45deg;
        }
        100% {
            left: 200%;
            rotate: 45deg;
        }
    }

    @media (max-width: 440px) {
        width: 180px;
        height: 50px;
        padding: 10px 12px;
        border-radius: 10px;
        font-size: 14px;
    }
`;

const HeadingContainer = styled.div.attrs({
    className: "col-lg-5 text-center text-lg-start",
})`
    padding: 0 10px;

    @media (min-width: 992px) {
        padding-left: 10%;
        width: 50%;
    }
`;

const HeroAnimationContainer = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    transform: translate(-20%, 10%);

    @media (min-width: 332px) {
        scale: 1.1;
        transform: translate(-25%, 12%);
    }
    @media (min-width: 440px) {
        scale: 1.2;
        transform: translate(-30%, 8%);
    }
    @media (min-width: 576px) {
        transform: translate(-34%, 8%);
    }
    @media (min-width: 768px) {
        scale: 1;
        transform: translate(-20%, 0%);
    }
    @media (min-width: 992px) {
        position: relative;
        scale: 1.3;
        transform: translate(-50%, 10%);
    }
    @media (min-width: 1200px) {
        scale: 1;
        transform: translate(-55%, -15%);
    }
`;

const CtaButtonDesktop = styled(CtaButton)`
    display: none;

    @media (min-width: 992px) {
        display: block;
        padding: 18px 12px;
        width: 300px;
        margin-top: 32px;
    }
`;

const CtaButtonMobile = styled(CtaButton)`
    display: block;
    width: 82%;
    margin-top: 28px;
    margin-bottom: 24px;
    align-self: center;
    font-size: 16px;

    @media (min-width: 992px) {
        display: none;
    }
`;

interface HandleDownloadCTAClickProps {
    isPageAuth: boolean;
    downloadURLsWithSuffix: {
        authPlayURL: string;
        authAppStoreURL: string;
        photosPlayURL: string;
        photosAppStoreURL: string;
    };
}

const handleDownloadCTAClick = ({
    isPageAuth,
    downloadURLsWithSuffix,
}: HandleDownloadCTAClickProps): void => {
    const os = getDeviceOS();

    const androidDownloadLink = isPageAuth
        ? downloadURLsWithSuffix.authPlayURL
        : downloadURLsWithSuffix.photosPlayURL;
    const iosDownloadLink = isPageAuth
        ? downloadURLsWithSuffix.authAppStoreURL
        : downloadURLsWithSuffix.photosAppStoreURL;
    const downloadSectionID = isPageAuth ? "download-auth" : "download";

    if (os === OS.Android) {
        window.location.href = androidDownloadLink;
    } else if (os === OS.IOS) {
        window.location.href = iosDownloadLink;
    } else {
        if (isPageAuth) {
            const downloadSection = document.getElementById(downloadSectionID);
            if (downloadSection) {
                downloadSection.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                });
            }
        } else {
            window.location.href = "https://web.ente.io";
        }
    }
};

interface DownloadCTADesktopProps {
    buttonText: string;
    downloadURLsWithSuffix: {
        authPlayURL: string;
        authAppStoreURL: string;
        photosPlayURL: string;
        photosAppStoreURL: string;
    };
}

const DownloadCTADesktop: React.FC<DownloadCTADesktopProps> = (props) => {
    const isPageAuth = props.buttonText === PrimaryDownloadCTAText.AUTH;
    const downloadURLsWithSuffix = props.downloadURLsWithSuffix;

    const { t } = useTranslation();

    return (
        <CtaButtonDesktop
            id={`${isPageAuth ? "auth" : "photos"}-desktop-download-button-hero-section`}
            onClick={() =>
                handleDownloadCTAClick({ isPageAuth, downloadURLsWithSuffix })
            }
        >
            {isPageAuth
                ? props.buttonText
                : t("EP_INDEX_HERO_GET_STARTED_BUTTON")}
        </CtaButtonDesktop>
    );
};

interface DownloadCTAMobileProps {
    buttonText: string;
    downloadURLsWithSuffix: {
        authPlayURL: string;
        authAppStoreURL: string;
        photosPlayURL: string;
        photosAppStoreURL: string;
    };
}

const DownloadCTAMobile: React.FC<DownloadCTAMobileProps> = (props) => {
    const isPageAuth = props.buttonText === PrimaryDownloadCTAText.AUTH;
    const downloadURLsWithSuffix = props.downloadURLsWithSuffix;

    return (
        <CtaButtonMobile
            id={`${isPageAuth ? "auth" : "photos"}-mobile-download-button-hero-section`}
            onClick={() =>
                handleDownloadCTAClick({ isPageAuth, downloadURLsWithSuffix })
            }
        >
            {props.buttonText}
        </CtaButtonMobile>
    );
};

export default function HeroSection(props: IProps) {
    const isAuthProp = props.headingText === "Ente Auth";

    const downloadURLsWithSuffix = {
        authPlayURL: URLwithUTMSuffix(DownloadURL.authGooglePlay),
        authAppStoreURL: URLwithUTMSuffix(DownloadURL.authAppStore),
        photosPlayURL: URLwithUTMSuffix(DownloadURL.googlePlay),
        photosAppStoreURL: URLwithUTMSuffix(DownloadURL.appStore),
    };

    const { t } = useTranslation();

    return (
        <Container>
            <BackDrop
                backdropBackground={props.backdropBackground}
                backdropBackgroundImage={props.backdropBackgroundImage}
            >
                <HeadingContainer>
                    {!isAuthProp && (
                        <div className="ten-gb-free-label-desktop">
                            <img src={greenHeart} width={"14px"} />{" "}
                            {t("EP_INDEX_HERO_10GB_FREE_FOREVER")}
                        </div>
                    )}
                    <Heading>
                        {isAuthProp ? (
                            props.headingText
                        ) : (
                            <Trans
                                i18nKey="EP_INDEX_HERO_TITLE"
                                components={{
                                    1: <br />,
                                }}
                            />
                        )}
                    </Heading>
                    <Paragraph isAuthProp={isAuthProp}>
                        {props.paragraphText}
                    </Paragraph>
                    {!isAuthProp && (
                        <div className="ten-gb-free-label-mobile">
                            <img src={greenHeart} width={"14px"} />{" "}
                            {t("EP_INDEX_HERO_10GB_FREE_FOREVER")}
                        </div>
                    )}
                    <DownloadCTADesktop
                        buttonText={props.buttonText}
                        downloadURLsWithSuffix={downloadURLsWithSuffix}
                    />
                </HeadingContainer>
                <ImageContainer>
                    <HeroDuckyImage
                        duckyShieldColor={props.duckyShieldColor}
                        isAuthProp={isAuthProp}
                    />
                    <Picture
                        aspectRatio={{
                            light: props.images[0].fluid.aspectRatio,
                        }}
                    >
                        <Image
                            fetch-priority="high"
                            srcSet={props.images[0].fluid.srcSetWebp}
                            alt="Ente App Screenshot"
                        />
                    </Picture>
                    <HeroAnimationContainer>
                        <HeroAnimation />
                    </HeroAnimationContainer>
                </ImageContainer>
            </BackDrop>
            <DownloadCTAMobile
                buttonText={props.buttonText}
                downloadURLsWithSuffix={downloadURLsWithSuffix}
            />
        </Container>
    );
}
